import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Diatonic-function lexicon entries (Phase 1.5 — analyzer-lexicon consolidation).
 * Copy authored by Bowie in the task brief and locked verbatim — do not edit
 * without Bowie review.
 *
 * Symbol conventions match `lexicon-entries.ts`:
 *   - Roman numerals: uppercase major-quality, lowercase minor-quality.
 *   - Diminished: ° suffix (Unicode).
 *   - Flat degrees: Unicode ♭ in display; ASCII `flat-` in id.
 *
 * Tunasong's parallel-major reference frame applies — minor's diatonic
 * 3rd/6th/7th are ♭III/♭VI/♭VII (see bowie/reference_roman_label_convention.md).
 *
 * The two-character casing distinction (function-I major vs function-i minor)
 * is the canonical disambiguator; do not normalize.
 */
export const FUNCTION_LEXICON_ENTRIES: LexiconEntry[] = [
  // ───────── Major-key diatonic (7) ─────────
  {
    id: 'function-I',
    category: 'function',
    displayName: 'I (tonic)',
    displayNameShort: 'I',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Home base — the most stable chord, where everything lands.',
      intermediate: 'I — the tonic; the chord of rest and home.',
      advanced: "I (tonic) — point of repose; the key's center of gravity.",
    },
    tagLabel: { beginner: 'home', intermediate: 'tonic', advanced: 'I' },
  },
  {
    id: 'function-ii',
    category: 'function',
    displayName: 'ii (supertonic)',
    displayNameShort: 'ii',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Pulls toward the dominant — a step on the way to tension.',
      intermediate: 'ii — the supertonic; predominant function.',
      advanced: 'ii (supertonic) — predominant; canonical setup for V.',
    },
    tagLabel: { beginner: 'rising', intermediate: 'supertonic', advanced: 'ii' },
  },
  {
    id: 'function-iii',
    category: 'function',
    displayName: 'iii (mediant)',
    displayNameShort: 'iii',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A soft, contemplative chord between home and the brighter chords.',
      intermediate: 'iii — the mediant; weak predominant or tonic-substitute.',
      advanced: 'iii (mediant) — weak predominant; shares two tones with both I and V.',
    },
    tagLabel: { beginner: 'middle', intermediate: 'mediant', advanced: 'iii' },
  },
  {
    id: 'function-IV',
    category: 'function',
    displayName: 'IV (subdominant)',
    displayNameShort: 'IV',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Lifts away from home — a sense of motion forward.',
      intermediate: 'IV — the subdominant; predominant function.',
      advanced: 'IV (subdominant) — predominant; plagal motion back to I.',
    },
    tagLabel: { beginner: 'lift', intermediate: 'subdominant', advanced: 'IV' },
  },
  {
    id: 'function-V',
    category: 'function',
    displayName: 'V (dominant)',
    displayNameShort: 'V',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'The strongest tension chord — pulls home.',
      intermediate: 'V — the dominant; pulls toward the tonic.',
      advanced: 'V (dominant) — dominant function; authentic resolution to I.',
    },
    tagLabel: { beginner: 'tense', intermediate: 'dominant', advanced: 'V' },
  },
  {
    id: 'function-vi',
    category: 'function',
    displayName: 'vi (submediant)',
    displayNameShort: 'vi',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'A bittersweet chord — the relative-minor anchor.',
      intermediate: 'vi — the submediant; relative minor of I.',
      advanced: 'vi (submediant) — relative minor; tonic-prolongation function.',
    },
    tagLabel: { beginner: 'minor home', intermediate: 'submediant', advanced: 'vi' },
  },
  {
    id: 'function-vii-dim',
    category: 'function',
    displayName: 'vii° (leading-tone)',
    displayNameShort: 'vii°',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Tense and unresolved — wants to lean home.',
      intermediate: 'vii° — the leading-tone chord; weak dominant function.',
      advanced: 'vii° (leading-tone diminished) — weak dominant; voice-leads to I.',
    },
    tagLabel: { beginner: 'lean', intermediate: 'leading-tone', advanced: 'vii°' },
  },

  // ───────── Minor-key diatonic (7) ─────────
  {
    id: 'function-i',
    category: 'function',
    displayName: 'i (minor tonic)',
    displayNameShort: 'i',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Home base — the minor home chord.',
      intermediate: 'i — the tonic; minor home.',
      advanced: 'i (minor tonic) — point of repose in minor.',
    },
    tagLabel: { beginner: 'minor home', intermediate: 'minor tonic', advanced: 'i' },
  },
  {
    id: 'function-ii-dim',
    category: 'function',
    displayName: 'ii° (minor supertonic)',
    displayNameShort: 'ii°',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Tense — sets up the dominant.',
      intermediate: 'ii° — the supertonic; predominant in minor.',
      advanced: 'ii° (supertonic diminished) — predominant; canonical lead-in to V.',
    },
    tagLabel: { beginner: 'tense lift', intermediate: 'supertonic dim', advanced: 'ii°' },
  },
  {
    id: 'function-flat-III',
    category: 'function',
    displayName: '♭III (mediant)',
    displayNameShort: '♭III',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Bright color — the relative-major anchor.',
      intermediate: '♭III — the mediant; relative major of i.',
      advanced: '♭III (mediant) — relative major; tonic-prolongation in minor.',
    },
    tagLabel: { beginner: 'bright', intermediate: 'mediant', advanced: '♭III' },
  },
  {
    id: 'function-iv',
    category: 'function',
    displayName: 'iv (minor subdominant)',
    displayNameShort: 'iv',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Tender lift — soft motion away from home.',
      intermediate: 'iv — the subdominant; predominant in minor.',
      advanced: 'iv (subdominant) — predominant; plagal-minor motion to i.',
    },
    tagLabel: { beginner: 'tender lift', intermediate: 'subdominant', advanced: 'iv' },
  },
  {
    id: 'function-v',
    category: 'function',
    displayName: 'v (natural-minor dominant)',
    displayNameShort: 'v',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Modal motion — natural minor's softer pull home.",
      intermediate: 'v — the natural-minor dominant; modal resolution to i.',
      advanced: 'v (natural-minor dominant) — lacks the leading tone; modal cadence rather than authentic.',
    },
    tagLabel: { beginner: 'modal pull', intermediate: 'minor dom', advanced: 'v' },
  },
  {
    id: 'function-flat-VI',
    category: 'function',
    displayName: '♭VI (submediant)',
    displayNameShort: '♭VI',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Warm and cinematic — a tender lift up.',
      intermediate: '♭VI — the submediant; weak tonic-prolongation.',
      advanced: '♭VI (submediant) — tonic-prolongation function; classical deceptive-cadence target in minor.',
    },
    tagLabel: { beginner: 'warm', intermediate: 'submediant', advanced: '♭VI' },
  },
  {
    id: 'function-flat-VII',
    category: 'function',
    displayName: '♭VII (subtonic)',
    displayNameShort: '♭VII',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Modal motion — rocky, folky color.',
      intermediate: '♭VII — the subtonic; modal motion below i.',
      advanced: '♭VII (subtonic) — modal motion; rock/folk idiom; can precede a return to i.',
    },
    tagLabel: { beginner: 'rocky', intermediate: 'subtonic', advanced: '♭VII' },
  },
]
